// data-fit LLC · DecisionFlow · Track 3b · S287
// NotificationPreferences.jsx
// API: v1.29 §43 — Notification Preferences Domain
// Auth: useAuth() direct — ARCH-2 compliant (credentials: 'include', no Bearer header)
//
// Two views in one component:
//   1. "My Preferences" — all authenticated users (§43.1 GET, §43.2 PATCH)
//   2. "Tenant Defaults" — comp_admin only (§43.3 GET, §43.4 PATCH)
//
// View 2 tab is hidden for non-comp_admin users.
// Each view maintains independent loading / saving / error state.
//
// Event code labels re-use EVENT_CODE_MAP from NotificationCenter.jsx pattern.
// Channels: 'inapp' (In-App) and 'email' (Email) — no other values.
// digest_frequency: 'daily', 'weekly', or null (immediate).
//
// OI-WS02-PREF-UI — S287 Track 3b

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { Search } from 'lucide-react'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHANNEL_LABELS = { inapp: 'In-App', email: 'Email' }

const EVENT_CODE_MAP = {
  'dispute.raised':            { label: 'Dispute Raised',         icon: '⚠️'  },
  'dispute.resolved':          { label: 'Dispute Resolved',       icon: '✅'  },
  'amendment.committed':       { label: 'Amendment Committed',    icon: '📝'  },
  'statement_batch.released':  { label: 'Statements Released',    icon: '📄'  },
  'calculation_run.completed': { label: 'Calculation Completed',  icon: '✅'  },
  'calculation_run.failed':    { label: 'Calculation Failed',     icon: '❌'  },
  'user.created':              { label: 'User Created',           icon: '👤'  },
  'auth.lockout':              { label: 'Account Locked',         icon: '🔒'  },
}
const getEventLabel = (code) => EVENT_CODE_MAP[code]?.icon
  ? `${EVENT_CODE_MAP[code].icon} ${EVENT_CODE_MAP[code].label}`
  : code

const DIGEST_OPTIONS = [
  { value: '',       label: 'Immediate' },
  { value: 'daily',  label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' },
]

// ---------------------------------------------------------------------------
// API helpers — match NotificationCenter.jsx patterns exactly
// ---------------------------------------------------------------------------

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error
      ? payload
      : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getErrorMessage = (err) => {
  if (err?.error) return err.error.message || `Error (${err.error.code})`
  return 'An unexpected error occurred.'
}

// ---------------------------------------------------------------------------
// Helpers — local state manipulation
// ---------------------------------------------------------------------------

/**
 * Build a lookup map keyed by `${event_code}::${channel}` from a preferences list.
 * Used to track pending edits without mutating server state.
 */
const buildPrefMap = (preferences) => {
  const map = {}
  for (const row of preferences) {
    map[`${row.event_code}::${row.channel}`] = { ...row }
  }
  return map
}

// ---------------------------------------------------------------------------
// Sub-component: UserPreferenceTable
// §43.1 GET + §43.2 PATCH
// ---------------------------------------------------------------------------

function UserPreferenceTable() {
  const [preferences, setPreferences]   = useState([])
  const [pendingEdits, setPendingEdits] = useState({})  // key → {is_active, digest_enabled, digest_frequency}
  const [isLoading, setIsLoading]       = useState(false)
  const [isSaving, setIsSaving]         = useState(false)
  const [loadError, setLoadError]       = useState(null)
  const [saveError, setSaveError]       = useState(null)
  const [saveSuccess, setSaveSuccess]   = useState(false)

  const loadPreferences = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setSaveSuccess(false)
    try {
      const response = await fetch('/api/v1/notification-preferences/user', {
        method: 'GET',
        credentials: 'include',
      })
      const payload = await parseApiResponse(response)
      setPreferences(payload.preferences || [])
      setPendingEdits(buildPrefMap(payload.preferences || []))
    } catch (err) {
      setLoadError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { loadPreferences() }, [loadPreferences])

  const handleToggle = (eventCode, channel, field, value) => {
    const key = `${eventCode}::${channel}`
    setSaveSuccess(false)
    setSaveError(null)
    setPendingEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }))
  }

  const handleDigestChange = (eventCode, channel, value) => {
    const key = `${eventCode}::${channel}`
    setSaveSuccess(false)
    setSaveError(null)
    const digest_enabled   = value !== ''
    const digest_frequency = value !== '' ? value : null
    setPendingEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], digest_enabled, digest_frequency },
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    const payload = Object.entries(pendingEdits).map(([key, vals]) => {
      const [event_code, channel] = key.split('::')
      return {
        event_code,
        channel,
        is_active:       vals.is_active,
        digest_enabled:  vals.digest_enabled  ?? false,
        digest_frequency: vals.digest_frequency ?? null,
      }
    })

    try {
      const response = await fetch('/api/v1/notification-preferences/user', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: payload }),
      })
      const data = await parseApiResponse(response)
      setPreferences(data.preferences || [])
      setPendingEdits(buildPrefMap(data.preferences || []))
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(getErrorMessage(err))
    } finally {
      setIsSaving(false)
    }
  }

  // S579 — search + scroll
  const [search, setSearch] = useState('')

  // Group preferences by event_code for table layout
  const eventCodes = useMemo(() => {
    const all = [...new Set(preferences.map((p) => p.event_code))].sort()
    const q = search.toLowerCase()
    if (!q) return all
    return all.filter((code) => getEventLabel(code).toLowerCase().includes(q) || code.toLowerCase().includes(q))
  }, [preferences, search])

  if (isLoading) {
    return (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Loading preferences…
      </p>
    )
  }

  if (loadError) {
    return (
      <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
        {loadError}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Control which notifications you receive and how. Changes take effect immediately.
      </p>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <input type="text" placeholder="Filter events…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none" />
      </div>

      {/* Preference table */}
      <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <table className="w-full text-tok-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--color-border))]">
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] w-64">Event</th>
              {['inapp', 'email'].map((ch) => (
                <th key={ch} className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                  {CHANNEL_LABELS[ch]}
                </th>
              ))}
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Email Delivery
              </th>
            </tr>
          </thead>
          <tbody>
            {eventCodes.map((code) => {
              const inappKey  = `${code}::inapp`
              const emailKey  = `${code}::email`
              const inappEdit = pendingEdits[inappKey]
              const emailEdit = pendingEdits[emailKey]

              return (
                <tr key={code} className="border-b border-[rgb(var(--color-border))] last:border-0">
                  <td className="px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] font-medium">
                    {getEventLabel(code)}
                  </td>

                  {/* In-app toggle */}
                  <td className="px-4 py-3">
                    {inappEdit ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={inappEdit.is_active ?? false}
                          onChange={(e) => handleToggle(code, 'inapp', 'is_active', e.target.checked)}
                          className="h-4 w-4 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
                        />
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {inappEdit.is_active ? 'On' : 'Off'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                    )}
                  </td>

                  {/* Email toggle */}
                  <td className="px-4 py-3">
                    {emailEdit ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailEdit.is_active ?? false}
                          onChange={(e) => handleToggle(code, 'email', 'is_active', e.target.checked)}
                          className="h-4 w-4 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
                        />
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {emailEdit.is_active ? 'On' : 'Off'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                    )}
                  </td>

                  {/* Email digest frequency — only active when email is on */}
                  <td className="px-4 py-3">
                    {emailEdit ? (
                      <select
                        value={
                          emailEdit.digest_enabled
                            ? (emailEdit.digest_frequency || 'daily')
                            : ''
                        }
                        onChange={(e) => handleDigestChange(code, 'email', e.target.value)}
                        disabled={!emailEdit.is_active}
                        className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-xs disabled:opacity-40"
                      >
                        {DIGEST_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Save feedback */}
      {saveError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
          {saveError}
        </div>
      )}
      {saveSuccess && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
          Preferences saved.
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-component: TenantPreferenceTable
// §43.3 GET + §43.4 PATCH — comp_admin only
// ---------------------------------------------------------------------------

function TenantPreferenceTable() {
  const [preferences, setPreferences]   = useState([])
  const [pendingEdits, setPendingEdits] = useState({})
  const [isLoading, setIsLoading]       = useState(false)
  const [isSaving, setIsSaving]         = useState(false)
  const [loadError, setLoadError]       = useState(null)
  const [saveError, setSaveError]       = useState(null)
  const [saveSuccess, setSaveSuccess]   = useState(false)

  const loadPreferences = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setSaveSuccess(false)
    try {
      const response = await fetch('/api/v1/notification-preferences/tenant', {
        method: 'GET',
        credentials: 'include',
      })
      const payload = await parseApiResponse(response)
      setPreferences(payload.preferences || [])
      setPendingEdits(buildPrefMap(payload.preferences || []))
    } catch (err) {
      setLoadError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { loadPreferences() }, [loadPreferences])

  const handleToggle = (eventCode, channel, value) => {
    const key = `${eventCode}::${channel}`
    setSaveSuccess(false)
    setSaveError(null)
    setPendingEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], is_active: value },
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    const payload = Object.entries(pendingEdits).map(([key, vals]) => {
      const [event_code, channel] = key.split('::')
      return {
        event_code,
        channel,
        is_active: vals.is_active,
      }
    })

    try {
      const response = await fetch('/api/v1/notification-preferences/tenant', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: payload }),
      })
      const data = await parseApiResponse(response)
      setPreferences(data.preferences || [])
      setPendingEdits(buildPrefMap(data.preferences || []))
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(getErrorMessage(err))
    } finally {
      setIsSaving(false)
    }
  }

  // S579 — search + scroll
  const [search, setSearch] = useState('')

  const eventCodes = useMemo(() => {
    const all = [...new Set(preferences.map((p) => p.event_code))].sort()
    const q = search.toLowerCase()
    if (!q) return all
    return all.filter((code) => getEventLabel(code).toLowerCase().includes(q) || code.toLowerCase().includes(q))
  }, [preferences, search])

  if (isLoading) {
    return (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Loading tenant defaults…
      </p>
    )
  }

  if (loadError) {
    return (
      <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
        {loadError}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Set org-wide notification defaults. These inform users' initial preference settings
        — they do not override individual user preferences or gate delivery directly.
      </p>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <input type="text" placeholder="Filter events…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none" />
      </div>

      <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <table className="w-full text-tok-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--color-border))]">
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] w-64">Event</th>
              {['inapp', 'email'].map((ch) => (
                <th key={ch} className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                  {CHANNEL_LABELS[ch]}
                </th>
              ))}
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Platform Default
              </th>
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Overridden
              </th>
            </tr>
          </thead>
          <tbody>
            {eventCodes.map((code) => {
              const inappKey  = `${code}::inapp`
              const emailKey  = `${code}::email`
              const inappEdit = pendingEdits[inappKey]
              const emailEdit = pendingEdits[emailKey]
              // Use inapp row for platform_default / has_tenant_override display
              const meta = inappEdit || emailEdit || {}

              return (
                <tr key={code} className="border-b border-[rgb(var(--color-border))] last:border-0">
                  <td className="px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] font-medium">
                    {getEventLabel(code)}
                  </td>

                  {/* In-app tenant toggle */}
                  <td className="px-4 py-3">
                    {inappEdit ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={inappEdit.is_active ?? false}
                          onChange={(e) => handleToggle(code, 'inapp', e.target.checked)}
                          className="h-4 w-4 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
                        />
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {inappEdit.is_active ? 'On' : 'Off'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                    )}
                  </td>

                  {/* Email tenant toggle */}
                  <td className="px-4 py-3">
                    {emailEdit ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailEdit.is_active ?? false}
                          onChange={(e) => handleToggle(code, 'email', e.target.checked)}
                          className="h-4 w-4 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
                        />
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {emailEdit.is_active ? 'On' : 'Off'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                    )}
                  </td>

                  {/* Platform default badge */}
                  <td className="px-4 py-3">
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {inappEdit?.platform_default ? '✅ On' : '⬜ Off'}
                      {' / '}
                      {emailEdit?.platform_default ? '✅ On' : '⬜ Off'}
                    </span>
                  </td>

                  {/* Override indicator */}
                  <td className="px-4 py-3">
                    {(inappEdit?.has_tenant_override || emailEdit?.has_tenant_override) ? (
                      <span className="inline-flex items-center rounded-full bg-[rgb(var(--color-primary))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-primary))]">
                        Custom
                      </span>
                    ) : (
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Default</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {saveError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
          {saveError}
        </div>
      )}
      {saveSuccess && (
        <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
          Tenant defaults saved.
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save Tenant Defaults'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main export — NotificationPreferences
// ---------------------------------------------------------------------------

export default function NotificationPreferences() {
  // useAuth() — token confirmed from NotificationCenter.jsx pattern.
  // userType not confirmed exposed by hook — probe tenant endpoint instead
  // to determine comp_admin status: 200 = comp_admin, 403 = not comp_admin.
  // eslint-disable-next-line no-unused-vars
  const { token } = useAuth()

  const [canManageTenant, setCanManageTenant] = useState(false)

  useEffect(() => {
    const probe = async () => {
      try {
        const r = await fetch('/api/v1/notification-preferences/tenant', {
          method: 'GET',
          credentials: 'include',
        })
        if (r.ok) setCanManageTenant(true)
        // 403 = not comp_admin — leave false silently
      } catch (_) {
        // Network error — leave tab hidden
      }
    }
    probe()
  }, [])

  const [activeTab, setActiveTab] = useState('user')

  const tabs = [
    { id: 'user',   label: 'My Preferences' },
    ...(canManageTenant ? [{ id: 'tenant', label: 'Tenant Defaults' }] : []),
  ]

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div>
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">
          Notification Preferences
        </h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
          Manage which notifications you receive for in-app alerts and emails.
        </p>
      </div>

      {/* Tab bar — only visible when comp_admin (has both tabs) */}
      {canManageTenant && (
        <div className="flex gap-1 rounded-lg bg-[rgb(var(--color-surface-muted))] p-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'rounded-md px-4 py-1.5 text-tok-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] shadow-sm'
                  : 'text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Active view */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
        {activeTab === 'user'   && <UserPreferenceTable />}
        {activeTab === 'tenant' && canManageTenant && <TenantPreferenceTable />}
      </div>
    </div>
  )
}
